
import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { isPrimaryDomain } from '@/lib/primary-domains'
import { 
  Mail, 
  TrendingUp, 
  Zap,
  AlertTriangle,
  CheckCircle,
  Database,
  Activity,
  Users,
  Globe,
  BarChart3,
  PieChart,
  RefreshCw,
  XCircle
} from 'lucide-react'
import { Mailbox } from '@/hooks/useMailboxes'

interface MailboxMetricsProps {
  mailboxes: Mailbox[]
  onWarmupStatusClick?: (status: string) => void
  activeWarmupFilter?: string | null
  onStatusClick?: (status: string) => void
  activeStatusFilter?: string | null
  onTotalMailboxesClick?: () => void
  onActiveMailboxesClick?: () => void
  onDeactivatedMailboxesClick?: () => void
  onDailyCapacityClick?: () => void
  onSmartleadSyncedClick?: () => void
  onPrimaryDomainsClick?: () => void
  performSync?: (platform: string) => void
  isSyncing?: boolean
  progress?: any
  stats?: any
  isBackgroundSyncRunning?: boolean
  schedulerStatus?: any
  /** Render only specific section: 'infrastructure' = Email Infrastructure + Warmup Details, 'sync' = Smartlead Two-Way Sync */
  section?: 'infrastructure' | 'sync'
}

export function MailboxMetrics({ 
  mailboxes, 
  onWarmupStatusClick, 
  activeWarmupFilter, 
  onStatusClick, 
  activeStatusFilter,
  onTotalMailboxesClick,
  onActiveMailboxesClick,
  onDeactivatedMailboxesClick,
  onDailyCapacityClick,
  onSmartleadSyncedClick,
  onPrimaryDomainsClick,
  performSync,
  isSyncing,
  progress,
  stats,
  isBackgroundSyncRunning,
  schedulerStatus,
  section
}: MailboxMetricsProps) {
  // Helper function to render cards with automatic grid layout
  const renderAutoGridCards = (
    items: Array<[string, number]>, 
    renderCard: (item: [string, number], index: number) => React.ReactNode
  ) => {
    return (
      <div className={`grid gap-4 ${
        items.length <= 2 ? 'grid-cols-2' : 
        items.length <= 3 ? 'grid-cols-3' : 
        items.length <= 4 ? 'grid-cols-4' : 
        items.length <= 5 ? 'grid-cols-5' : 
        items.length <= 6 ? 'grid-cols-6' : 
        'grid-cols-7'
      }`}>
        {items.map((item, index) => renderCard(item, index))}
      </div>
    )
  }

  // Use smartlead_id as the real "connected" indicator (not api_sync_status which was broken)
  const totalMailboxes = mailboxes.length
  const activeMailboxes = mailboxes.filter(m => m.status !== 'DEACTIVATED').length
  const deactivatedMailboxes = mailboxes.filter(m => m.status === 'DEACTIVATED').length
  const connectedToSmartlead = mailboxes.filter(m => m.smartlead_id != null && m.status !== 'DEACTIVATED').length
  const connectedToMasterinbox = mailboxes.filter(m => (m as any).masterinbox_connected === true && m.status !== 'DEACTIVATED').length

  // Calculate daily capacity from message_per_day where connected to SmartLead
  const dailyCapacity = mailboxes
    .filter(m => m.smartlead_id != null && m.status !== 'DEACTIVATED')
    .reduce((sum, m) => sum + (m.message_per_day || 0), 0)

  // Use smartlead_id for connected mailboxes (the real source of truth)
  const successfulSyncMailboxes = mailboxes.filter(m => m.smartlead_id != null && m.status !== 'DEACTIVATED')
  
  
  // Calculate warmup metrics using the already filtered successfulSyncMailboxes
  const warmupActive = successfulSyncMailboxes.filter(m => m.warmup_status === 'ACTIVE').length
  const warmupInactive = successfulSyncMailboxes.filter(m => {
    // Skip primary domains - they don't need warmup
    if (isPrimaryDomain(m.email)) {
      return false
    }
    
    // Skip deactivated accounts - they're not actively being used
    if (m.status === 'DEACTIVATED') {
      return false
    }
    
    return m.warmup_status === 'INACTIVE'
  }).length
  const warmupNull = successfulSyncMailboxes.filter(m => {
    if (isPrimaryDomain(m.email)) return false
    return m.warmup_status === null || m.warmup_status === undefined
  }).length
  
  // Calculate primary domains count
  const primaryDomains = successfulSyncMailboxes.filter(m => isPrimaryDomain(m.email)).length
  
  // Warmup active count for headline display
  const warmingUp = successfulSyncMailboxes.filter(m => m.warmup_status === 'ACTIVE').length
  
  // Calculate health metrics based on warmup_reputation (same logic as health distribution)
  const reputationDistribution = successfulSyncMailboxes.reduce((acc, m) => {
    const reputation = m.warmup_reputation ? parseInt(m.warmup_reputation) : 0
    
    if (reputation >= 80) acc.excellent++
    else if (reputation >= 60) acc.good++
    else acc.needsAttention++
    return acc
  }, { excellent: 0, good: 0, needsAttention: 0 })

  // Calculate provider distribution from successfully synced mailboxes
  const providerCounts = successfulSyncMailboxes.reduce((acc, m) => {
    const provider = m.provider || 'Unknown'
    const normalizedProvider = provider.toUpperCase()
    if (normalizedProvider.includes('GMAIL') || normalizedProvider.includes('GOOGLE')) {
      acc.GMAIL = (acc.GMAIL || 0) + 1
    } else if (normalizedProvider.includes('OUTLOOK') || normalizedProvider.includes('MICROSOFT')) {
      acc.OUTLOOK = (acc.OUTLOOK || 0) + 1
    } else if (normalizedProvider.includes('SMTP')) {
      acc.SMTP = (acc.SMTP || 0) + 1
    } else {
      acc.OTHER = (acc.OTHER || 0) + 1
    }
    return acc
  }, {} as Record<string, number>)

  // Calculate primary and secondary mailboxes from successfully synced only
  const primaryMailboxes = successfulSyncMailboxes.filter(m => 
    m.provider?.toLowerCase().includes('gmail') || 
    m.provider?.toLowerCase().includes('google') ||
    (m.warmup_status && m.warmup_status !== 'DISABLED' && parseInt(m.warmup_reputation || '0') >= 90)
  ).length
  
  const secondaryMailboxes = successfulSyncMailboxes.length - primaryMailboxes

  // Calculate status breakdowns
  const statusCounts = mailboxes.reduce((acc, m) => {
    const status = m.status || 'NULL'
    acc[status] = (acc[status] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  const syncStatusCounts = mailboxes.reduce((acc, m) => {
    const syncStatus = m.api_sync_status || 'NULL'
    acc[syncStatus] = (acc[syncStatus] || 0) + 1
    return acc
  }, {} as Record<string, number>)



  // Render based on section prop
  if (section === 'infrastructure') {
    // Email Infrastructure card with Warmup Details (matches "Domain Infrastructure" position)
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Mail className="h-5 w-5 text-primary" />
            </div>
            <CardTitle className="text-xl font-semibold">Email Infrastructure</CardTitle>
          </div>
          <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
            <Activity className="h-3 w-3 mr-1" />
            Live
          </Badge>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Headline Numbers */}
          <div className="grid grid-cols-3 gap-4">
            <div
              className={`p-5 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900 ${onActiveMailboxesClick ? 'cursor-pointer hover:bg-emerald-100/60 transition-colors' : ''}`}
              onClick={onActiveMailboxesClick}
            >
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  <div className="text-4xl font-bold text-emerald-600 dark:text-emerald-400">{activeMailboxes}</div>
                </div>
                <div className="text-sm font-semibold text-foreground">Active Mailboxes</div>
                <div className="text-xs text-muted-foreground mt-1">{deactivatedMailboxes} deactivated &middot; {totalMailboxes} total</div>
              </div>
            </div>
            <div
              className={`p-5 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900 ${onSmartleadSyncedClick ? 'cursor-pointer hover:bg-blue-100/60 transition-colors' : ''}`}
              onClick={onSmartleadSyncedClick}
            >
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Database className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  <div className="text-4xl font-bold text-blue-600 dark:text-blue-400">{connectedToSmartlead}</div>
                </div>
                <div className="text-sm font-semibold text-foreground">Connected to SmartLead</div>
                <div className="text-xs text-muted-foreground mt-1">{activeMailboxes > 0 ? Math.round((connectedToSmartlead / activeMailboxes) * 100) : 0}% of active &middot; {dailyCapacity.toLocaleString()} emails/day</div>
              </div>
            </div>
            <div
              className="p-5 rounded-lg bg-purple-50 dark:bg-purple-950/20 border border-purple-100 dark:border-purple-900"
            >
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Mail className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  <div className="text-4xl font-bold text-purple-600 dark:text-purple-400">{connectedToMasterinbox}</div>
                </div>
                <div className="text-sm font-semibold text-foreground">Connected to MasterInbox</div>
                <div className="text-xs text-muted-foreground mt-1">{activeMailboxes > 0 ? Math.round((connectedToMasterinbox / activeMailboxes) * 100) : 0}% of active</div>
              </div>
            </div>
          </div>

          {/* Warmup Details */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-1.5 rounded-md bg-amber-100 dark:bg-amber-900/30">
                <Zap className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-base font-semibold text-foreground">Warmup Details</h3>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <div className="p-1.5 rounded-md bg-emerald-100 dark:bg-emerald-900/30">
                      <Activity className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <button onClick={() => onWarmupStatusClick?.('ACTIVE')} className={`text-3xl font-bold transition-all ${activeWarmupFilter === 'ACTIVE' ? 'text-emerald-700 dark:text-emerald-300' : 'text-foreground'} ${onWarmupStatusClick ? 'cursor-pointer hover:text-emerald-600' : ''}`}>{warmupActive}</button>
                  </div>
                  <div className="text-sm font-medium text-muted-foreground">Warming Up</div>
                  <div className="text-xs text-muted-foreground/70 mt-1">{connectedToSmartlead > 0 ? Math.round((warmupActive / connectedToSmartlead) * 100) : 0}% of connected</div>
                </div>
              </div>
              <div className="p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <div className="p-1.5 rounded-md bg-amber-100 dark:bg-amber-900/30">
                      <AlertTriangle className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                    </div>
                    <button onClick={() => onWarmupStatusClick?.('INACTIVE')} className={`text-3xl font-bold transition-all ${activeWarmupFilter === 'INACTIVE' ? 'text-amber-700 dark:text-amber-300' : 'text-foreground'} ${onWarmupStatusClick ? 'cursor-pointer hover:text-amber-600' : ''}`}>{warmupInactive + warmupNull}</button>
                  </div>
                  <div className="text-sm font-medium text-muted-foreground">Warmup Inactive</div>
                  <div className="text-xs text-muted-foreground/70 mt-1">Paused + No status</div>
                </div>
              </div>
              <div className={`p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors ${onPrimaryDomainsClick ? 'cursor-pointer' : ''}`} onClick={onPrimaryDomainsClick}>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <div className="p-1.5 rounded-md bg-blue-100 dark:bg-blue-900/30">
                      <Globe className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="text-3xl font-bold text-foreground">{primaryDomains}</div>
                  </div>
                  <div className="text-sm font-medium text-muted-foreground">Primary Mailboxes</div>
                  <div className="text-xs text-muted-foreground/70 mt-1">Business domain mailboxes</div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (section === 'sync') {
    // Smartlead Two-Way Sync only (for bottom placement)
    if (!performSync) return null
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <RefreshCw className="h-5 w-5 text-primary" />
            </div>
            <CardTitle className="text-xl font-semibold">Smartlead Two-Way Sync</CardTitle>
          </div>
          <Button onClick={() => performSync('smartlead')} disabled={isSyncing} className="gap-2" size="lg">
            <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
            {isSyncing ? 'Syncing...' : 'Sync Now'}
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-sm text-muted-foreground">Sync mailbox data with Smartlead. Dashboard always wins conflicts.</div>
          {isSyncing && progress && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{progress.message}</span>
                <span className="font-medium">{progress.percentage.toFixed(0)}%</span>
              </div>
              <div className="w-full bg-secondary rounded-full h-2 overflow-hidden">
                <div className="bg-primary h-full transition-all duration-300 ease-in-out" style={{ width: `${progress.percentage}%` }} />
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="text-center p-4 rounded-lg border bg-card">
              <div className="text-2xl font-bold text-foreground">{stats?.totalConnections || 0}</div>
              <div className="text-xs text-muted-foreground mt-1">Connections</div>
            </div>
            <div className="text-center p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900">
              <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{stats?.syncedConnections || 0}</div>
              <div className="text-xs text-muted-foreground mt-1">Synced</div>
            </div>
            <div className="text-center p-4 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-100 dark:border-amber-900">
              <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{stats?.conflictConnections || 0}</div>
              <div className="text-xs text-muted-foreground mt-1">Conflicts</div>
            </div>
            <div className="text-center p-4 rounded-lg border bg-card">
              <div className="text-2xl font-bold text-muted-foreground">{stats?.failedConnections || 0}</div>
              <div className="text-xs text-muted-foreground mt-1">Failed</div>
            </div>
          </div>
          <div className="flex items-center justify-between p-4 rounded-lg border bg-card">
            <div className="flex items-center gap-2">
              <div className={`h-2 w-2 rounded-full ${isBackgroundSyncRunning ? 'bg-emerald-500 animate-pulse' : 'bg-muted-foreground'}`} />
              <span className="text-sm font-medium">Background Sync {isBackgroundSyncRunning ? 'Active' : 'Inactive'}</span>
            </div>
            {schedulerStatus?.lastSyncAt && (
              <span className="text-xs text-muted-foreground">Last: {new Date(schedulerStatus.lastSyncAt).toLocaleTimeString()}</span>
            )}
          </div>
        </CardContent>
      </Card>
    )
  }

  // Default: render nothing (section prop should always be specified)
  return null
}
